import { Type } from 'class-transformer';
import { Model } from './model';
import { Call } from './call';
import { Dispute } from './dispute';
import { Email } from './email';
import { EmailThread } from './email_thread';
import { InteractionClassification } from './interaction_classification';
import { Invoice } from './invoice';
import { OutreachWorkflowRun } from './outreach_workflow';
import { Partner } from './partner';
import { PaymentPromise } from './payment_promise';
import { TaskTrigger } from './task_trigger';
import { User } from './user';
import type { DisputeContextData, TaskCall } from './task_data';
import dayjs from 'dayjs';
import { humanize } from '@stuut/common/src/utils';
import { Priority } from '@stuut/common/src/constants';
import { ModelWithAttachments } from './attachment';

import {
  TaskData,
  AcknowledgeTaskData,
  ContextTaskData,
  ResponseTaskData,
  SummaryTaskData,
  ContactTaskData,
  PhoneContextJSON,
  EmailContextJSON,
  SMSContextJSON,
  type SMSJSON,
} from './task_data';

import { TaskAction } from './task_action';
import { TaskRequirement } from './task_requirement';
import { KnowledgeCitation } from './knowledge_citation';

export enum TaskType {
  AutoResponseEmail = 'auto_response_email',
  AutoResponseSms = 'auto_response_sms',
  CallAnalysis = 'call_analysis',
  DisableContact = 'disable_contact',
  RecommendationNewContact = 'recommendation_new_contact',
  ManualCall = 'manual_call',
  BrokenPromiseFollowUp = 'broken_promise_follow_up',
  PaymentPromise = 'payment_promise',
  Escalation = 'escalation',
  PauseStateUpdate = 'pause_state_update',
  DisputeActivate = 'dispute_activate',
  DisputeCreate = 'dispute_create',
  DisputeUpdate = 'dispute_update',
  DisputeResolution = 'dispute_resolution',
  SmsSendFailure = 'sms_send_failure',
}

export enum TaskStatus {
  ARCHIVED = 'archived',
  CLOSED = 'closed',
  COMPLETED = 'completed',
  FAILED = 'failed',
  OPEN = 'open',
  PROCESSING = 'processing',
}

const DEFAULT_CUSTOM_DESCRIPTION_KEY: Record<string, string> = {
  payment_promise: 'no_expected_action',
};

interface CustomDescriptionItem {
  icon: string;
  label: string;
  title: string;
}

export const TASK_CUSTOM_DESCRIPTION: Record<string, Record<string, CustomDescriptionItem>> = {
  payment_promise: {
    complete: {
      icon: 'far fa-calendar-exclamation text-destructive',
      label: 'Needs completion',
      title: 'Complete promise to pay tasks',
    },
    acknowledge: {
      icon: 'fas fa-sparkle text-agent',
      label: 'Acknowledge new dates',
      title: 'Acknowledge payment promise dates',
    },
    no_expected_action: {
      icon: 'far fa-check-circle text-muted-foreground',
      label: 'Review and complete',
      title: 'Review payment promise dates',
    },
  },
};

// const TaskTypeLabelMap = {
//   [TaskType.AutoResponseEmail]: 'Email response',
//   [TaskType.CallAnalysis]: 'Call response',
//   [TaskType.DisableContact]: 'Disabled Contact',
//   [TaskType.RecommendationNewContact]: 'New contact',
//   [TaskType.ManualCall]: 'Manual Call',
//   [TaskType.BrokenPromiseFollowUp]: 'Broken Payment Promise',
//   [TaskType.PaymentPromise]: 'Payment Promise',
//   [TaskType.Escalation]: 'Escalation',
//   [TaskType.PauseStateUpdate]: 'Pause State Update',
// };

export const TaskTypeTransformMap = {
  auto_response_email: 'Review response',
  auto_response_sms: 'Review SMS response',
  call_analysis: 'Call follow up',
  payment_promise: 'Payment promises',
  partner: 'Customer',
  complete: 'Needs completion',
  acknowledge: 'Acknowledge',
  no_expected_action: 'Review',
  [TaskType.DisputeActivate]: 'Draft Dispute',
  [TaskType.DisputeCreate]: 'Potential Dispute',
  [TaskType.DisputeUpdate]: 'Dispute Update',
  [TaskType.DisputeResolution]: 'Dispute Resolution Found',
  [TaskType.SmsSendFailure]: 'SMS Send Failure',
  [TaskType.PauseStateUpdate]: 'Dunning state update',
};

export const TaskActionTransformMap = {
  auto_response_email: 'Send a response',
  auto_response_sms: 'Send a response',
  call_analysis: 'Send a response',
  payment_promise: 'Review payment promises',
  partner: 'Customer',
  complete: 'Needs completion',
  acknowledge: 'Acknowledge',
  no_expected_action: 'Review',
  [TaskType.DisputeActivate]: 'Activate dispute',
  [TaskType.DisputeCreate]: 'Review and complete dispute',
  [TaskType.DisputeUpdate]: 'Update dispute',
  [TaskType.DisputeResolution]: 'Review dispute resolution',
  [TaskType.SmsSendFailure]: 'SMS Send Failure',
};

export const TaskTypeIconClassMap = {
  auto_response_email: 'text-agent',
  auto_response_sms: 'text-agent',
  call_analysis: 'text-blue-500',
  disable_contact: 'text-destructive',
  recommendation_new_contact: 'text-agent',
  manual_call: 'text-warning',
  broken_promise_follow_up: 'text-error',
  payment_promise: 'text-green-600',
  pause_state_update: 'text-warning-700',
  escalation: 'text-error',
  [TaskType.SmsSendFailure]: 'text-destructive',
};

export const TaskTypeIconMap = {
  auto_response_email: 'far fa-envelope-circle-check',
  auto_response_sms: 'far fa-message-sms',
  dispute: 'far fa-balance-scale',
  call_analysis: 'far fa-phone',
  disable_contact: 'far fa-circle-xmark',
  recommendation_new_contact: 'far fa-id-badge',
  manual_call: 'fas fa-bell',
  broken_promise_follow_up: 'fas fa-heart-crack',
  payment_promise: 'fas fa-envelope-open-dollar',
  pause_state_update: 'far fa-circle-pause',
  escalation: 'fas fa-chart-line-up',
  [TaskType.DisputeActivate]: 'far fa-balance-scale',
  [TaskType.DisputeUpdate]: 'far fa-comment-pen',
  [TaskType.DisputeCreate]: 'far fa-balance-scale',
  [TaskType.DisputeResolution]: 'far fa-balance-scale',
  [TaskType.SmsSendFailure]: 'far fa-message-xmark',
};

const TaskIconClassMap = {
  recommendation_new_contact: 'text-agent',
  disable_contact: 'text-destructive',
  call_analysis: 'text-agent',
  auto_response_email: 'text-agent',
  auto_response_sms: 'text-agent',
};

const AutoResponseEmailIconClassMap = {
  refund_request: 'text-destructive',
  payment_promise: 'text-agent',
  payment_inquiry: 'text-notice',
};

const TaskIconMap = {
  auto_response_email: 'fa-regular fa-envelope-circle-check',
  auto_response_sms: 'fa-regular fa-message-sms',
  recommendation_new_contact: 'fa-regular fa-id-badge',
  disable_contact: 'fa-regular fa-circle-xmark',
  call_analysis: 'fa-regular fa-file-waveform',
  broken_promise_follow_up: 'fas fa-heart-crack',
};

const AutoResponseEmailIconMap = {
  payment_promise: 'fa-solid fa-money-bill-trend-up',
  refund_request: 'fa-solid fa-dollar-sign',
  payment_inquiry: 'fa-regular fa-envelope-open-dollar',
};

export const getTaskType = (type): string => {
  if (!type) return '';

  return TaskTypeTransformMap[type]
    ? humanize(TaskTypeTransformMap[type] as string)
    : humanize(type);
};

export const getTaskActionLabel = (type): string => {
  if (!type) return '';

  return TaskActionTransformMap[type]
    ? humanize(TaskActionTransformMap[type] as string)
    : humanize(type);
};

export class Task extends ModelWithAttachments {
  object = 'task';

  @Type(() => User)
  assignee?: User;

  @Type(() => TaskData, {
    discriminator: {
      property: 'type',
      subTypes: [
        { value: AcknowledgeTaskData, name: 'acknowledge' },
        { value: ContextTaskData, name: 'context' },
        { value: SummaryTaskData, name: 'summary' },
        { value: ResponseTaskData, name: 'response' },
        { value: ContactTaskData, name: 'contact' },
      ],
    },
    keepDiscriminatorProperty: true,
  })
  data:
    | (
        | AcknowledgeTaskData
        | ContextTaskData
        | SummaryTaskData
        | ResponseTaskData
        | ContactTaskData
      )[]
    | null;

  @Type(() => TaskAction)
  completed_action: TaskAction | null;

  @Type(() => Invoice)
  invoice: Invoice | null;

  @Type(() => Invoice)
  invoices: Invoice[] | null;

  @Type(() => TaskTrigger)
  trigger_event: TaskTrigger;

  action_taken?: string;

  assignee_id?: string;

  @Type(() => Call)
  call: Call | null;

  call_id?: string;

  close_reason?: string;

  description: string;

  @Type(() => InteractionClassification)
  interaction_classifications: InteractionClassification[] | null;

  @Type(() => OutreachWorkflowRun)
  outreach_stage_run: OutreachWorkflowRun;

  error_reason: string;

  expected_action: string;

  group_id: string;

  invoice_id: string;

  label: string;

  is_group: boolean;

  is_subtask: boolean;

  dispute_id: string;

  @Type(() => Dispute)
  dispute: Dispute | null;

  organization_id: string;

  partner_id: string | null;

  @Type(() => Partner)
  partner: Partner | null;

  priority: Priority;

  source: string;

  status: TaskStatus;

  @Type(() => Email)
  email: Email | null;

  @Type(() => EmailThread)
  email_thread: EmailThread | null;

  email_id: string;

  email_thread_id: string;

  @Type(() => KnowledgeCitation)
  knowledge_citations: KnowledgeCitation[];

  thread_id: string;

  @Type(() => Task)
  subtasks: Task[] | null;

  @Type(() => Task)
  related_tasks: Task[] | null;

  @Type(() => TaskRequirement)
  hitl_questions: TaskRequirement[] | null;

  trigger_event_id: string;

  payment_promise_id: string | null;

  @Type(() => PaymentPromise)
  payment_promise: PaymentPromise | null;

  total_in_dispute_amount: number;

  total_in_overdue_dispute_amount: number;

  type: string;

  get isEmailSource(): boolean {
    return this.source === 'email';
  }

  get isCallSource(): boolean {
    return this.source === 'call';
  }

  get isSmsSource(): boolean {
    return this.source === 'sms';
  }

  get openRelatedTasks() {
    return this.related_tasks?.filter((task) => task?.isOpen);
  }

  get openCompletedRelatedTasks() {
    return this.related_tasks?.filter((task) => task?.isOpen || task?.isCompleted);
  }

  get nonArchivedRelatedTasks() {
    return this.related_tasks?.filter((task) => !task?.isArchived);
  }

  get sortedSubtasks() {
    return this.subtasks?.slice().sort((a, b) => b.created_at.unix() - a.created_at.unix());
  }

  get canCompletePromiseSubtask(): boolean {
    return this.is_subtask && this.isExpectsComplete && Boolean(this.invoice?.promised_to_pay_date);
  }

  get hasCompleteablePromiseSubtasks(): boolean {
    return this.openSubtasks?.some((task) => task.canCompletePromiseSubtask) ?? false;
  }

  get isAllSubtasksHavePromiseDates(): boolean {
    return this.openActiveSubtasks?.every((task) => task.invoice?.promised_to_pay_date) as boolean;
  }

  get activeSubtasks() {
    const invoiceIds: string[] = [];

    return this.sortedSubtasks
      ?.filter((task) => !task.isArchived)
      .reduce<Task[]>((acc, subtask) => {
        const id = subtask.invoice_id || '';

        if (id && invoiceIds.includes(id)) return acc;

        invoiceIds.push(id);
        acc.push(subtask);

        return acc;
      }, []);
  }

  get openActiveSubtasks() {
    return this.activeSubtasks?.filter((task) => task.isOpen);
  }

  get closedOrCompletedActiveSubtasks() {
    return this.activeSubtasks?.filter((task) => task.isCompleted || task.isClosed);
  }

  get openSubtasks() {
    return this.subtasks?.filter((task) => task.isOpen);
  }

  get completedSubtasks() {
    return this.subtasks?.filter((task) => task.isCompleted);
  }

  get taskType() {
    return getTaskType(this.type);
  }

  get taskTypeIcon() {
    if (this.type === 'payment_promise' && !this.isReplyRequired) {
      return 'far fa-handshake';
    }
    return TaskTypeIconMap[this.type] as string;
  }

  get taskTypeIconClass() {
    return TaskTypeIconClassMap[this.type] as string;
  }

  get accountData() {
    return this.data?.find((data) => data.type === 'account');
  }

  get acknowledgeTaskData() {
    return this.data?.find((data) => data.type === 'acknowledge') as
      | AcknowledgeTaskData
      | undefined;
  }

  get disputeTaskData() {
    return this.contextData?.data as DisputeContextData;
  }

  get customDescription(): CustomDescriptionItem | undefined {
    const expectedAction = this.expected_action || DEFAULT_CUSTOM_DESCRIPTION_KEY[this.type];

    return TASK_CUSTOM_DESCRIPTION[this.type]?.[expectedAction];
  }

  get classifications() {
    if (this.type === 'manual_call') return ['manual_call'];
    if (this.type === 'broken_promise_follow_up') return ['broken_promise'];
    if (this.type === 'pause_state_update') {
      // derive from label
      const label = this.label ?? '';
      if (/enable/i.test(label)) return ['dunning_enabled'];
      if (/expired|restored/i.test(label)) return ['dunning_restored'];
      return ['dunning_paused'];
    }

    const classificationNames = this.interaction_classifications?.map((c) => c.classification);

    if (this.isDisableContact && !classificationNames?.length) {
      if (this.isCallSource) return ['call_failed'];
      if (this.isEmailSource) return ['email_bounced'];
    }

    return classificationNames || [];
  }

  get interactionClassification(): InteractionClassification | undefined {
    return this.interaction_classifications?.[0];
  }

  get summaryData() {
    return this.data?.find((data) => data.type === 'summary') as SummaryTaskData;
  }

  get contactData() {
    return this.data?.find((data) => data.type === 'contact') as ContactTaskData;
  }

  get contextData() {
    return this.data?.find((data) => data.type === 'context') as ContextTaskData | undefined;
  }

  get callData() {
    if (this.contextData?.data instanceof PhoneContextJSON) {
      return this.contextData?.data?.call;
    }

    return undefined;
  }

  get disputeNumber() {
    return this.dispute?.number;
  }

  get disputeData() {
    if (this.contextData?.data) {
      return this.contextData?.data as DisputeContextData;
    }

    return undefined;
  }

  get emailData() {
    if (this.contextData?.data instanceof EmailContextJSON) {
      return this.contextData?.data?.email;
    }

    return undefined;
  }

  get smsData(): SMSJSON | undefined {
    // Check task-level source since the transformer may not have
    // properly instantiated SMSContextJSON if source wasn't set on the data item
    if (this.isSmsSource && this.contextData?.data) {
      return (this.contextData.data as SMSContextJSON)?.sms;
    }

    if (this.contextData?.data instanceof SMSContextJSON) {
      return this.contextData?.data?.sms;
    }

    return undefined;
  }

  get smsThreadId(): string | undefined {
    return this.smsData?.sms_thread_id;
  }

  get promisesData() {
    return this.acknowledgeTaskData?.data?.promises;
  }

  get newPromisedPayments() {
    return this.promisesData?.filter((promise) => promise.is_promised_date_updated) ?? [];
  }

  get responseData() {
    return this.data?.find((data) => data.type === 'response') as ResponseTaskData | undefined;
  }

  get responseSource() {
    return this.responseData?.source || this.contextData?.source;
  }

  get isContactManagementType() {
    return this.type === 'recommendation_new_contact' || this.type === 'disable_contact';
  }

  get classificationReason() {
    if (this.isBrokenPromiseFollowUp) {
      return 'The customer has missed their promised payment date on the following invoices; please review the related communication and invoice details to follow up.';
    }

    if (this.isNewContact) {
      return `Review and fill out any additional details if you want to add ${this.contactData?.data?.email_address}`;
    }

    return this.interactionClassification?.reason;
  }

  get classificationRecommendation() {
    return this.interactionClassification?.action_recommendation;
  }

  get classificationEvidence() {
    return this.interactionClassification?.evidenceText;
  }

  get classificationCallouts() {
    return this.interactionClassification?.callouts;
  }

  get classificationKeywords() {
    return this.interactionClassification?.keywords;
  }

  get isReplyRequired(): boolean {
    return Boolean(
      this.isBrokenPromiseFollowUp ||
      this.isMessageForwardingRequest ||
      this.isAutoResponseEmail ||
      this.isAutoResponseSms ||
      this.isCallAnalysis ||
      this.interactionClassification?.is_reply_required,
    );
  }

  get isActionRequired(): boolean {
    return this.interactionClassification?.is_action_required ?? false;
  }

  get taskComponentType() {
    if (this.isManualCall) return 'manual_call';
    if (this.isBrokenPromiseFollowUp) return 'email_response';

    if (this.isReplyRequired) {
      return 'email_response';
    }

    if (this.acknowledgeTaskData?.id) return 'acknowledge';

    return '';
  }

  get isBrokenPromiseFollowUp() {
    return this.type === 'broken_promise_follow_up';
  }

  get isPauseStateUpdate(): boolean {
    return this.type === 'pause_state_update';
  }

  get isMessageForwardingRequest(): boolean {
    return this.classifications.includes('message_forwarding_request');
  }

  get taskTriggerType() {
    if (this.isCallSource) return 'call';
    if (this.isEmailSource) return 'email';
    if (this.isBrokenPromiseFollowUp) return 'payment promise';

    return undefined;
  }

  get taskIcon() {
    if (this.isManualCall) return 'fa-regular fa-phone';
    if (this.isAutoResponseEmail) return this.autoResponseEmailIcon;

    return TaskIconMap[this.type];
  }

  get taskIconClass() {
    if (this.isAutoResponseEmail) return this.autoResponseEmailIconClass;
    return TaskIconClassMap[this.type] ?? 'text-muted-foreground';
  }

  get autoResponseEmailIcon() {
    if (!this.isAutoResponseEmail) return TaskIconMap[this.type];

    let icon;

    Object.keys(AutoResponseEmailIconMap).forEach((key) => {
      if (!icon && this.classifications.includes(key)) {
        icon = AutoResponseEmailIconMap[key];
      }
    });

    return icon ?? TaskIconMap[this.type];
  }

  get autoResponseEmailIconClass() {
    if (!this.isAutoResponseEmail) return TaskIconClassMap[this.type];

    let iconClass;

    Object.keys(AutoResponseEmailIconClassMap).forEach((key) => {
      if (!iconClass && this.classifications.includes(key)) {
        iconClass = AutoResponseEmailIconClassMap[key];
      }
    });

    return iconClass ?? TaskIconClassMap[this.type];
  }

  get isDone(): boolean {
    return this.isCompleted || this.isFailed || this.isProcessing;
  }

  get isOpen(): boolean {
    return this.status === TaskStatus.OPEN;
  }

  get isCompleted(): boolean {
    return this.status === TaskStatus.COMPLETED;
  }

  get isClosed(): boolean {
    return this.status === TaskStatus.CLOSED;
  }

  get isProcessing(): boolean {
    return this.status === TaskStatus.PROCESSING;
  }

  get isArchived(): boolean {
    return this.status === TaskStatus.ARCHIVED;
  }

  get isFailed(): boolean {
    return this.status === TaskStatus.FAILED;
  }

  get isAutoResponseEmail(): boolean {
    return this.type === 'auto_response_email';
  }

  get isAutoResponseSms(): boolean {
    return this.type === 'auto_response_sms';
  }

  get isSmsSendFailure(): boolean {
    return TaskType.SmsSendFailure === this.type;
  }

  get isCallAnalysis(): boolean {
    return this.type === 'call_analysis';
  }

  get isDisputeCreate(): boolean {
    return TaskType.DisputeCreate === this.type;
  }

  get isDisputeActivate(): boolean {
    return TaskType.DisputeActivate === this.type;
  }

  get isDisputeAnyCreate(): boolean {
    return this.isDisputeActivate || this.isDisputeCreate;
  }

  get isAnyDispute(): boolean {
    return (
      this.isDisputeActivate ||
      this.isDisputeCreate ||
      this.isDisputeUpdate ||
      this.isDisputeResolution
    );
  }

  get isDisputeUpdate(): boolean {
    return TaskType.DisputeUpdate === this.type;
  }

  get isDisputeResolution(): boolean {
    return TaskType.DisputeResolution === this.type;
  }

  get isInactiveDispute(): boolean {
    return this.isDisputeCreate || this.isDisputeActivate || this.isDisputeUpdate;
  }

  get isManualCall(): boolean {
    return this.type === 'manual_call';
  }

  get isDisableContact(): boolean {
    return this.type === 'disable_contact';
  }

  get isEscalation(): boolean {
    return this.type === 'escalation';
  }

  get isNewContact(): boolean {
    return this.type === 'recommendation_new_contact';
  }

  get isPaymentPromiseGroup(): boolean {
    return this.is_group && this.type === 'payment_promise';
  }

  get isCompletePromiseGroup(): boolean {
    return this.isPaymentPromiseGroup && this.isExpectsComplete;
  }

  get isAcknowledgePaymentPromiseGroup(): boolean {
    return this.isPaymentPromiseGroup && this.isExpectsAcknowledge;
  }

  get isPaymentPromise(): boolean {
    return this.type === 'payment_promise';
  }

  // Action Types
  get isExpectsComplete(): boolean {
    return this.expected_action === 'complete';
  }

  get isExpectsCompleteWithAction(): boolean {
    return this.expected_action === 'complete_with_action';
  }

  get isExpectsAcknowledge(): boolean {
    return this.expected_action === 'acknowledge';
  }

  get isUrgent(): boolean {
    return this.priority === 'urgent';
  }

  get attachmentIds() {
    return this.attachments?.map((att) => att.id) || [];
  }

  get invoicesWithPdfIds() {
    return this.invoices?.filter((inv) => inv.pdf_id);
  }

  get attachableInvoices() {
    return this.invoicesWithPdfIds?.filter((inv) => !this.attachmentIds.includes(inv.pdf_id || ''));
  }

  get daysDiff(): number {
    const diff = this.created_at.diff(new Date(), 'days');
    return diff ? Math.abs(diff) : 0;
  }

  get hoursDiff(): number {
    const diff = this.created_at.diff(new Date(), 'hours');
    return diff ? Math.abs(diff) : 0;
  }

  get daysElapsed(): number {
    if (this.daysDiff === 1 && this.hoursDiff < 24) return 0;

    return this.daysDiff;
  }

  get timeToCompletion(): string | undefined {
    const completedAt = this.completed_action?.created_at;

    if (completedAt) {
      return dayjs(completedAt).from(this.created_at, true);
    }

    return undefined;
  }
}
